import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { BookOpen, Plus, FileText, Loader2, Trash2, ArrowRight, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { thesisApi } from '@/api/thesisApi';
import { ThemeToggle } from '@/components/ThemeToggle';

export default function ThesisProjects() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', research_question: '', language: 'bg' });

  useEffect(() => {
    if (!authStorage.isAuthorAuthenticated()) {
      navigate('/thesis/login');
      return;
    }
    setUser(authStorage.getAuthorUser());
  }, [navigate]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await thesisApi.listProjects(user.id);
      setProjects(data.projects || []);
    } catch (e) {
      toast.error('Грешка при зареждане на проекти: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Моля въведете заглавие');
      return;
    }
    setCreating(true);
    try {
      const project = await thesisApi.createProject(user.id, form);
      toast.success('Проектът е създаден');
      setCreateOpen(false);
      setForm({ title: '', research_question: '', language: 'bg' });
      navigate(`/thesis/${project.id}`);
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (projectId, title) => {
    if (!window.confirm(`Изтриване на "${title}"? Това действие е необратимо.`)) return;
    try {
      await thesisApi.deleteProject(user.id, projectId);
      toast.success('Проектът е изтрит');
      load();
    } catch (e) {
      toast.error('Грешка: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleLogout = () => {
    authStorage.clearAuthorAuth();
    navigate('/thesis/login');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-background to-background dark:from-slate-950 dark:via-background text-foreground thesis-brand">
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/thesis" className="flex items-center gap-3" data-testid="thesis-home-link">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-700 to-amber-600 flex items-center justify-center shadow-md">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-lg font-serif font-bold leading-none">ThesisRAG</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Doctoral Writing Suite</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user.email}</span>
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="thesis-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />Изход
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between mb-10 gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-serif font-bold mb-3 tracking-tight">
              Дисертационни <span className="text-teal-700 dark:text-teal-400">проекти</span>
            </h1>
            <p className="text-muted-foreground text-base max-w-xl">
              RAG-базирано писане на докторски дисертации с PubMed литература и Manubot цитати.
            </p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-thesis-project-btn" className="gap-2 bg-teal-700 hover:bg-teal-800 text-white shrink-0">
                <Plus className="w-4 h-4" /> Нов проект
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Нов дисертационен проект</DialogTitle>
                <DialogDescription>
                  Дефинирайте темата и езика на дисертацията. Можете да го промените по-късно.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-sm font-medium mb-1 block">Заглавие *</label>
                  <Input
                    placeholder="Напр. Скрининг на рак на гърдата при плътна тъкан"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="thesis-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Изследователски въпрос</label>
                  <Textarea
                    placeholder="Каква е ефикасността на MRI спрямо мамография при плътни гърди?"
                    value={form.research_question}
                    onChange={(e) => setForm({ ...form, research_question: e.target.value })}
                    data-testid="thesis-question-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Език</label>
                  <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                    <SelectTrigger data-testid="thesis-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bg">Български</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Отказ</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="thesis-create-submit-btn">
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Създай
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="text-center py-16 border-dashed border-2 border-stone-300 dark:border-slate-700 bg-stone-50/50 dark:bg-slate-900/50" data-testid="thesis-empty-state">
            <CardContent>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-100 to-amber-100 dark:from-teal-900 dark:to-amber-900 flex items-center justify-center mx-auto mb-5">
                <FileText className="w-8 h-8 text-teal-700 dark:text-teal-300" />
              </div>
              <p className="text-xl font-serif font-semibold mb-2">Започнете първата си дисертация</p>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Качете медицински статии, претърсете PubMed и нека ИИ напише академични глави с проверяеми цитати.
              </p>
              <Button onClick={() => setCreateOpen(true)} className="bg-teal-700 hover:bg-teal-800 text-white">
                <Plus className="w-4 h-4 mr-2" /> Нов проект
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="thesis-projects-grid">
            {projects.map((p) => (
              <Card key={p.id} className="group hover:shadow-lg hover:border-teal-300 dark:hover:border-teal-700 transition-all" data-testid={`thesis-project-card-${p.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg leading-tight font-serif">{p.title}</CardTitle>
                    <Badge variant="outline" className="shrink-0 border-teal-300 text-teal-700 dark:text-teal-400">{p.language === 'bg' ? 'BG' : 'EN'}</Badge>
                  </div>
                  <CardDescription className="line-clamp-2 mt-1">
                    {p.research_question || 'Без изследователски въпрос'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <span>{p.document_count || 0} документа</span>
                    <span>{p.chapter_count || 0} глави</span>
                  </div>
                  <div className="flex gap-2">
                    <Button asChild size="sm" className="flex-1 bg-teal-700 hover:bg-teal-800 text-white" data-testid={`thesis-open-${p.id}`}>
                      <Link to={`/thesis/${p.id}`}>Отвори <ArrowRight className="w-3 h-3 ml-1" /></Link>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id, p.title)} data-testid={`thesis-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
